import { useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { PtsBillingService, PtsProjectService } from "@/services/APIs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import BillingInvoiceFilters, {
  type BillingInvoiceFilterValues,
} from "./BillingInvoiceFilters";
import BillingInvoicePagination from "./BillingInvoicePagination";
import { BILLING_DEFAULT_PAGE_SIZE } from "./billingPaginationUtils";
import { DASHBOARD_LISTING_QUERY_KEY } from "./dashboardListUtils";
import { resolveProjectLabel } from "./billingListUtils";
import { PAYMENT_TRACKING_STATUS_OPTIONS } from "./types";
import { inr, invoiceStatusBadge } from "./utils";

const DASHBOARD_TABLE_COLUMN_COUNT = 10;

const DashboardTab = () => {
  const [filterValues, setFilterValues] = useState<BillingInvoiceFilterValues>({
    startDate: "",
    endDate: "",
    projectId: undefined,
    status: undefined,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(BILLING_DEFAULT_PAGE_SIZE);
  const { startDate, endDate, projectId, status } = filterValues;

  const handleFilterChange = (values: BillingInvoiceFilterValues) => {
    setFilterValues(values);
    setPage(1);
  };

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = projectOptions.map(option => ({
    value: option.value,
    label: option.label,
  }));

  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      ...DASHBOARD_LISTING_QUERY_KEY,
      startDate,
      endDate,
      projectId,
      status,
      page,
      pageSize,
    ],
    queryFn: () =>
      PtsBillingService.getDashboardListing(
        startDate,
        endDate,
        projectId,
        page,
        pageSize,
        status
      ),
  });

  const summary = dashboardData?.summary;
  const invoices = dashboardData?.invoices ?? [];

  const dashboardCards = [
    {
      label: "Pending Approval",
      value: summary?.pendingApproval ?? "0",
      sub: "Awaiting accountant",
      icon: Activity,
      gradientClass: "from-amber-200",
      iconBgClass: "bg-amber-500/10",
      iconClass: "text-amber-700",
    },
    {
      label: "Paid",
      value: summary?.paidApproval ?? "0",
      sub: "Approved & paid",
      icon: CheckCircle2,
      gradientClass: "from-green-200",
      iconBgClass: "bg-green-500/10",
      iconClass: "text-green-500",
    },
    {
      label: "Overdue",
      value: summary?.overdueApproval ?? "0",
      sub: "Past grace period",
      icon: AlertTriangle,
      gradientClass: "from-red-200",
      iconBgClass: "bg-red-500/10",
      iconClass: "text-red-600",
    },
    {
      label: "Total Revenue",
      value: summary?.totalRevenue ?? "—",
      sub: "Lifetime, this client",
      icon: FileText,
      gradientClass: "from-blue-200",
      iconBgClass: "bg-blue-500/10",
      iconClass: "text-blue-500",
    },
    {
      label: "Pending Revenue",
      value: summary?.pendingRevenue ?? "—",
      sub: "Outstanding amount",
      icon: Clock,
      gradientClass: "from-cyan-200",
      iconBgClass: "bg-cyan-500/10",
      iconClass: "text-cyan-700",
    },
  ];

  return (
    <div className="space-y-6">
      <BillingInvoiceFilters
        idPrefix="dashboard"
        values={filterValues}
        onChange={handleFilterChange}
        statusOptions={PAYMENT_TRACKING_STATUS_OPTIONS}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        {dashboardCards.map(card => (
          <Card key={card.label} className="relative overflow-hidden">
            <div
              className={`absolute right-0 top-0 h-24 w-24 rounded-bl-full ${card.gradientClass} to-transparent`}
            />
            <CardContent className="bg-white px-5 py-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-sm font-medium">
                    {card.label}
                  </p>
                  <p className="text-foreground mb-2 mt-1 text-lg font-bold tabular-nums md:text-[1.6rem]">
                    {isLoading ? "..." : card.value}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {card.sub}
                  </p>
                </div>
                <div className={`rounded-xl p-3 ${card.iconBgClass}`}>
                  <card.icon className={`h-5 w-5 ${card.iconClass}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-lg">Invoice Listing</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto px-6 pb-0 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-primary font-semibold">
                    Invoice #
                  </TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center align-middle">
                    Status
                  </TableHead>
                  <TableHead className="text-center align-middle">
                    Received
                  </TableHead>
                  <TableHead className="text-center align-middle">
                    Pending
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={DASHBOARD_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Loading dashboard invoices...
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={DASHBOARD_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Failed to load dashboard data
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={DASHBOARD_TABLE_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map(inv => (
                    <TableRow key={inv.id} className="hover:bg-muted/40">
                      <TableCell className="text-primary font-semibold">
                        {inv.id}
                      </TableCell>
                      <TableCell>{inv.client}</TableCell>
                      <TableCell>
                        {resolveProjectLabel(inv.project, projectSelectOptions)}
                      </TableCell>
                      <TableCell className="text-blue-600">
                        {inv.period}
                      </TableCell>
                      <TableCell className="text-right">
                        {inr(inv.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {inr(inv.gst)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {inr(inv.total)}
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <div className="flex justify-center">
                          {invoiceStatusBadge(inv.status)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center align-middle text-green-600">
                        {inr(inv.received ?? 0)}
                      </TableCell>
                      <TableCell className="text-center align-middle text-red-600">
                        {(inv.pending ?? 0) > 0 ? inr(inv.pending ?? 0) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <BillingInvoicePagination
            current={page}
            pageSize={pageSize}
            total={dashboardData?.count ?? 0}
            onChange={handlePaginationChange}
            disabled={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardTab;
